/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import ExcelJS from 'exceljs';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './HODDashboard.css';

// --- SVG Icons ---
const Icons = {
    Print: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>,
    Download: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>,
    Refresh: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>,
    Student: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
    Book: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>,
    Check: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>,
    Alert: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>,
    Info: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c'];

export default function HODDashboard() {
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentTerm, setCurrentTerm] = useState(1);
    const [activityFromDate, setActivityFromDate] = useState('');
    const [activityToDate, setActivityToDate] = useState('');
    const [selectedSubjectForGrade, setSelectedSubjectForGrade] = useState('');
    const [selectedTopGrade, setSelectedTopGrade] = useState('all');
    const [selectedSupportTableGrade, setSelectedSupportTableGrade] = useState('all');
    const [selectedSupportSubjectGrade, setSelectedSupportSubjectGrade] = useState('all');
    const [selectedSupportSubject, setSelectedSupportSubject] = useState('all');
    const [selectedRewardGrade, setSelectedRewardGrade] = useState('all');
    const [rewardAnnual, setRewardAnnual] = useState(false);
    const [activeSection, setActiveSection] = useState('overview');

    const compareGrades = (a, b) => {
        const aNum = Number(a);
        const bNum = Number(b);
        if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
            return aNum - bNum;
        }
        return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    };

    // Subject hooks must be declared before any early returns
    const subjectList = Array.from(new Set(((analyticsData && analyticsData.pass_rates_by_subject) || []).map(s => (s.subject_name || '').trim()))).filter(Boolean).sort();

    useEffect(() => {
        if (!selectedSubjectForGrade && subjectList.length) {
            setSelectedSubjectForGrade(subjectList[0]);
        }
    }, [subjectList, selectedSubjectForGrade]);

    const gradePerformanceData = useMemo(() => {
        if (!selectedSubjectForGrade) return [];
        const pass_rates = (analyticsData && analyticsData.pass_rates_by_subject) || [];
        const filtered = pass_rates.filter(s => (s.subject_name || '').trim() === selectedSubjectForGrade);
        const grouped = filtered.reduce((acc, row) => {
            const g = String(row.grade_level || '');
            const avg = Number(row.average_percentage) || Number(row.average || 0) || 0;
            const pass = Number(row.passed_count || row.passed_students || row.pass_rate || 0) || 0;
            const total = Number(row.total_students) || Number(row.student_count) || 0;
            if (!acc[g]) acc[g] = { grade_level: g, total_students: 0, total_avg: 0, passed: 0 };
            acc[g].total_students += total;
            acc[g].total_avg += avg * (total || 1);
            acc[g].passed += pass;
            return acc;
        }, {});

        const out = Object.keys(grouped).map(k => {
            const item = grouped[k];
            const avgPct = item.total_students > 0 ? Number((item.total_avg / item.total_students).toFixed(1)) : 0;
            const passRate = item.total_students > 0 ? Number(((item.passed / item.total_students) * 100).toFixed(1)) : 0;
            return { grade_level: k, average_percentage: avgPct, pass_rate: passRate };
        }).sort((a, b) => Number(a.grade_level) - Number(b.grade_level));

        return out;
    }, [selectedSubjectForGrade, analyticsData]);

    const fetchAnalytics = useCallback(async () => {
        try {
            setLoading(true);
            const authToken = localStorage.getItem('auth_token');
            const schoolId = localStorage.getItem('school_id');
            let url = `${process.env.REACT_APP_API_URL}/fetch_hod_analytics.php?school_id=${schoolId}` + (currentTerm ? `&term=${currentTerm}` : '');
            if (rewardAnnual) url += '&annual_awards=1';
            const response = await axios.get(url, { headers: { 'Authorization': `Bearer ${authToken}` } });

            console.log('Analytics Response:', response.data);

            if (response.data.success) {
                setAnalyticsData(response.data);
                setError(null);
            } else {
                setError(response.data.error || 'Failed to load analytics data');
            }
        } catch (err) {
            console.error('Analytics Fetch Error:', err.response?.data || err.message);
            setError(err.response?.data?.error || 'Failed to fetch analytics.');
        } finally {
            setLoading(false);
        }
    }, [currentTerm, rewardAnnual]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    const gradeOptions = useMemo(() => {
        const grades = new Set();
        const addGrades = (rows) => {
            (rows || []).forEach((row) => {
                const grade = String(row?.grade_level ?? row?.grade ?? '').trim();
                if (grade) {
                    grades.add(grade);
                }
            });
        };

        if (analyticsData) {
            addGrades(analyticsData.performance_by_grade);
            addGrades(analyticsData.top_performers_by_grade);
            addGrades(analyticsData.bottom_performers_by_grade);
            addGrades(analyticsData.students_needing_support_by_subject);
            addGrades(analyticsData.pass_rates_by_subject);
        }

        return Array.from(grades).sort(compareGrades);
    }, [analyticsData]);

    const supportSubjectOptions = useMemo(() => {
        const uniqueSubjects = new Map();

        (analyticsData?.students_needing_support_by_subject || []).forEach((subject) => {
            const subjectId = String(subject.subject_id || '').trim();
            const subjectName = String(subject.subject_name || '').trim();
            const gradeLevel = String(subject.grade_level || '').trim();
            const className = String(subject.class_name || '').trim();
            if (!subjectId || !subjectName) return;
            if (selectedSupportSubjectGrade !== 'all' && gradeLevel !== selectedSupportSubjectGrade) return;

            const key = subjectId;
            if (!uniqueSubjects.has(key)) {
                uniqueSubjects.set(key, {
                    value: subjectId,
                    label: `${subjectName} (Gr ${gradeLevel}${className ? `, ${className}` : ''})`
                });
            }
        });

        return Array.from(uniqueSubjects.values()).sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
    }, [analyticsData, selectedSupportSubjectGrade]);

    const filteredTopPerformersByGrade = useMemo(() => {
        return (analyticsData?.top_performers_by_grade || [])
            .filter((group) => selectedTopGrade === 'all' || String(group.grade_level) === selectedTopGrade)
            .map((group) => ({
                ...group,
                students: [...(group.students || [])].sort((a, b) => Number(b.average_percentage) - Number(a.average_percentage))
            }))
            .sort((a, b) => compareGrades(a.grade_level, b.grade_level));
    }, [analyticsData, selectedTopGrade]);

    const filteredSupportBySubject = useMemo(() => {
        return (analyticsData?.students_needing_support_by_subject || [])
            .filter((subject) => (selectedSupportSubjectGrade === 'all' || String(subject.grade_level) === selectedSupportSubjectGrade))
            .filter((subject) => selectedSupportSubject === 'all' || String(subject.subject_id) === selectedSupportSubject)
            .map((subject) => ({
                ...subject,
                students: [...(subject.students || [])].slice(0, 8)
            }))
            .sort((a, b) => compareGrades(a.grade_level, b.grade_level) || String(a.subject_name).localeCompare(String(b.subject_name), undefined, { numeric: true, sensitivity: 'base' }));
    }, [analyticsData, selectedSupportSubjectGrade, selectedSupportSubject]);

    const filteredSupportTableByGrade = useMemo(() => {
        return (analyticsData?.bottom_performers_by_grade || [])
            .filter((group) => selectedSupportTableGrade === 'all' || String(group.grade_level) === selectedSupportTableGrade)
            .map((group) => ({
                ...group,
                students: [...(group.students || [])].sort((a, b) => Number(a.average_percentage) - Number(b.average_percentage))
            }))
            .sort((a, b) => compareGrades(a.grade_level, b.grade_level));
    }, [analyticsData, selectedSupportTableGrade]);

    const rewardCriteria = analyticsData?.reward_criteria || {};

    const filteredRewardRecipients = useMemo(() => {
        const filterByGrade = (rows) => (rows || []).filter((row) => selectedRewardGrade === 'all' || String(row.grade_level) === selectedRewardGrade);

        return {
            academicExcellence: filterByGrade(rewardCriteria.academic_excellence?.recipients || []),
            outstandingAchievement: filterByGrade(rewardCriteria.outstanding_academic_achievement?.recipients || []),
            seniorColours: filterByGrade(rewardCriteria.senior_academic_colours?.recipients || []),
            topTenGroups: (analyticsData?.top_performers_by_grade || [])
                .filter((group) => selectedRewardGrade === 'all' || String(group.grade_level) === selectedRewardGrade)
                .map((group) => ({
                    ...group,
                    students: [...(group.students || [])].sort((a, b) => Number(b.average_percentage) - Number(a.average_percentage)).slice(0, 10)
                }))
                .sort((a, b) => compareGrades(a.grade_level, b.grade_level))
        };
    }, [analyticsData, rewardCriteria.academic_excellence?.recipients, rewardCriteria.outstanding_academic_achievement?.recipients, rewardCriteria.senior_academic_colours?.recipients, selectedRewardGrade]);

    // --- Chart Data Computations ---
    
    // Subject Ranking - by average performance
    const subjectRankingData = useMemo(() => {
        const data = (analyticsData?.pass_rates_by_subject || [])
            .reduce((acc, row) => {
                const subj = String(row.subject_name || '').trim();
                if (!subj) return acc;
                const weight = Number(row.valid_students || row.total_students || 0) || 0;
                const existing = acc.find(s => s.subject_name === subj);
                if (existing) {
                    existing.total_avg += Number(row.average_percentage || 0) * weight;
                    existing.weight += weight;
                } else {
                    acc.push({ subject_name: subj, total_avg: Number(row.average_percentage || 0) * weight, weight });
                }
                return acc;
            }, [])
            .map(s => ({ subject_name: s.subject_name, average_percentage: Number((s.total_avg / (s.weight || 1)).toFixed(1)) }))
            .sort((a, b) => Number(b.average_percentage) - Number(a.average_percentage));
        return data;
    }, [analyticsData]);

    // Top Performers vs Support Students Ratio
    const performanceRatioData = useMemo(() => {
        const supportCount = (analyticsData?.bottom_performers_by_grade || [])
            .reduce((sum, group) => sum + (group.students?.length || 0), 0);
        const totalStudents = (analyticsData?.overall_stats?.total_students || 0);
        const onTrack = totalStudents - supportCount;
        
        return [
            { name: 'On Track', value: Math.max(0, onTrack), fill: '#4CAF50' },
            { name: 'Needs Support', value: supportCount, fill: '#FF6B6B' }
        ];
    }, [analyticsData]);

    // Subject Performance Trend Data (current term across all subjects)
    const subjectTrendData = useMemo(() => {
        const trendMap = {};
        (analyticsData?.pass_rates_by_subject || []).forEach(row => {
            const subj = String(row.subject_name || '').trim();
            const grade = String(row.grade_level || '').trim();
            if (!subj || !grade) return;
            const weight = Number(row.valid_students || row.total_students || 0) || 0;
            const key = `${subj}`;
            if (!trendMap[key]) {
                trendMap[key] = { subject: subj, totalAvg: 0, weight: 0, passed: 0, valid: 0 };
            }
            trendMap[key].totalAvg += Number(row.average_percentage || 0) * weight;
            trendMap[key].passed += Number(row.passed_count || 0);
            trendMap[key].valid += Number(row.valid_students || row.total_students || 0);
            trendMap[key].weight += weight;
        });
        
        const trend = Object.values(trendMap)
            .map(t => ({
                name: t.subject,
                average: Number((t.totalAvg / (t.weight || 1)).toFixed(1)),
                passRate: t.valid > 0 ? Number(((t.passed / t.valid) * 100).toFixed(1)) : 0
            }))
            .sort((a, b) => Number(b.average) - Number(a.average));
        return trend;
    }, [analyticsData]);

    // Hide global sidebar while this dashboard is mounted
    useEffect(() => {
        try {
            document.body.classList.add('hide-sidebar');
        } catch (e) {}
        return () => {
            try {
                document.body.classList.remove('hide-sidebar');
            } catch (e) {}
        };
    }, []);

    if (loading) {
        return (
            <div className="hod-dashboard loading-state">
                <div className="spinner"></div>
                <p>Loading Analytics...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="hod-dashboard error-state">
                <p>{error}</p>
                <button onClick={fetchAnalytics} className="btn-retry">Retry</button>
            </div>
        );
    }

    const {
        overall_stats,
        pass_rates_by_subject,
        performance_by_grade,
        students_needing_support_by_subject,
        insights
    } = analyticsData;

    // Group subjects (coerce numeric fields and use API field names)

    const uniqueSubjectCount = new Set(
        (pass_rates_by_subject || [])
            .map(s => String(s.subject_name || '').trim().toLowerCase())
            .filter(Boolean)
    ).size;

    // Compute subject distribution as unique subjects per grade (avoid counting classes)
    const subjectDistributionData = (() => {
        const rows = (analyticsData && analyticsData.pass_rates_by_subject) || [];
        const seen = new Set();
        const counts = {};
        rows.forEach(r => {
            const grade = String(r.grade_level || '').trim();
            const subj = String(r.subject_name || '').trim();
            if (!grade || !subj) return;
            const key = `${grade}::${subj}`.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            counts[grade] = (counts[grade] || 0) + 1;
        });
        return Object.keys(counts).map(g => ({ grade_level: g, name: `Grade ${g}`, value: counts[g] }));
    })();

    // Get personalized greeting based on time of day
    const getGreeting = () => {
        const hour = new Date().getHours();
        const username = localStorage.getItem('username') || 'User';
        let greeting = '';

        if (hour < 12) {
            greeting = 'Good morning';
        } else if (hour < 17) {
            greeting = 'Good afternoon';
        } else {
            greeting = 'Good evening';
        }

        return `${greeting}, ${username}!`;
    };

    // Helpers for export: grading helpers
    const calculateLevel = (percentage) => {
        const rounded = Math.round(percentage);
        if (rounded >= 80) return 7;
        if (rounded >= 70) return 6;
        if (rounded >= 60) return 5;
        if (rounded >= 50) return 4;
        if (rounded >= 40) return 3;
        if (rounded >= 30) return 2;
        return 1;
    };

    // Export teacher-style statistics sheets for one or more terms
    const exportToExcelForTerms = async (terms) => {
        if (!analyticsData) return;

        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'Marksure HOD';
        workbook.created = new Date();

        const thinBorder = {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
        };

        const fillGrey = (argb) => ({
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb }
        });

        const styleRange = (ws, fromRow, toRow, fromCol, toCol, style) => {
            for (let r = fromRow; r <= toRow; r++) {
                for (let c = fromCol; c <= toCol; c++) {
                    const cell = ws.getCell(r, c);
                    if (style.font) cell.font = style.font;
                    if (style.fill) cell.fill = style.fill;
                    if (style.border) cell.border = style.border;
                    if (style.alignment) cell.alignment = style.alignment;
                }
            }
        };

        const subjects = analyticsData.pass_rates_by_subject || [];
        const usedSheetNames = new Set();
        const invalidSheetCharRegex = new RegExp('[\\\\/?*\\[\\]:]', 'g');

        const getUniqueSheetName = (baseName) => {
            const sanitized = String(baseName || 'Sheet').replace(invalidSheetCharRegex, ' ').trim() || 'Sheet';
            const base = sanitized.substring(0, 31);
            if (!usedSheetNames.has(base)) {
                usedSheetNames.add(base);
                return base;
            }

            let i = 2;
            while (i < 1000) {
                const suffix = ` (${i})`;
                const candidate = `${base.substring(0, 31 - suffix.length)}${suffix}`;
                if (!usedSheetNames.has(candidate)) {
                    usedSheetNames.add(candidate);
                    return candidate;
                }
                i++;
            }

            const fallback = `${Date.now()}`.substring(0, 31);
            usedSheetNames.add(fallback);
            return fallback;
        };

        // Preload student name map for lookups
        let allStudentsMap = {};
        try {
            const allRes = await axios.get(`${process.env.REACT_APP_API_URL}/fetch_all_students.php`);
            (allRes.data.students || []).forEach(s => { allStudentsMap[s.student_id] = s; });
        } catch (e) {
            allStudentsMap = {};
        }

        for (const term of terms) {
            for (const subj of subjects) {
                try {
                    const id = subj.subject_id;
                    const [classRes, secRes, marksRes] = await Promise.all([
                        axios.get(`${process.env.REACT_APP_API_URL}/fetch_class_marks.php?subject_id=${id}`),
                        axios.get(`${process.env.REACT_APP_API_URL}/task_sections.php?subject_id=${id}&term=${term}`),
                        axios.get(`${process.env.REACT_APP_API_URL}/section_marks.php?subject_id=${id}&term=${term}`)
                    ]);

                    const sections = secRes.data.sections || [];
                    const marks = marksRes.data.marks || [];
                    let students = (classRes.data && classRes.data.students) ? classRes.data.students.map(s => ({ ...s })) : [];

                    if (students.length === 0) {
                        const sids = Array.from(new Set(marks.map(m => String(m.student_id))));
                        students = sids.map(sid => {
                            const s = allStudentsMap[sid] || { student_id: sid, first_name: '', last_name: '' };
                            return { student_id: sid, first_name: s.first_name || '', last_name: s.last_name || '' };
                        });
                    }

                    const className = classRes?.data?.class_name || '';
                    const grade = classRes?.data?.grade || subj.grade_level || '';
                    const sheetName = getUniqueSheetName(`T${term} ${subj.subject_name} Gr${grade} ${className}`);
                    const ws = workbook.addWorksheet(sheetName);

                    const nameLower = (subj.subject_name || '').toLowerCase();
                    const isHomeLanguage = nameLower.includes('home language') || nameLower.includes('huistaal');
                    const passMark = isHomeLanguage ? 50 : 40;

                    ws.addRow(['STATISTIC AND DIAGNOSTIC ANALYSIS OF MARKS']);
                    ws.addRow([]);
                    ws.addRow(['SUBJECT:', subj.subject_name, '', '', 'MARK:', sections.reduce((sum, s) => sum + (parseFloat(s.max_mark) || 0), 0)]);
                    ws.addRow(['EXAMINER:', '', '', '', 'GRADE:', grade]);
                    ws.addRow(['Term', term]);
                    ws.addRow([]);

                    ws.addRow([
                    'Section', 'Total in class', 'Total written', 'Class average',
                    'Highest mark', 'Lowest mark', 'Total failed', '% Failures',
                    'Total that passed', '% Passed',
                    '0 - 29', '30 - 39', '40 - 49', '50 - 59', '60 - 69', '70 - 79', '80 - 100',
                    'Total (1-7 = Total/Total written)'
                    ]);

                    const sectionRows = [];

                    sections.forEach(section => {
                        const max = parseFloat(section.max_mark) || 100;
                        const sectionMarks = marks
                        .filter(m => Number(m.section_id) === Number(section.id) && m.mark !== '' && m.mark != null)
                        .map(m => parseFloat(m.mark))
                        .filter(v => !isNaN(v));

                        const totalWritten = sectionMarks.length;
                        const levelDist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
                        let passed = 0;
                        let failed = 0;
                        let percentSum = 0;

                        sectionMarks.forEach(mark => {
                            const pct = max > 0 ? (mark / max) * 100 : 0;
                            percentSum += pct;

                            const lvl = calculateLevel(pct);
                            levelDist[lvl] += 1;

                            if (Math.round(pct) >= passMark) {
                                passed += 1;
                            } else {
                                failed += 1;
                            }
                        });

                        const total17 = Object.values(levelDist).reduce((a, b) => a + b, 0);
                        sectionRows.push([
                        `${section.section_name} (${max})`,
                        students.length,
                        totalWritten,
                        totalWritten > 0 ? `${(percentSum / totalWritten).toFixed(1)}%` : '',
                        totalWritten > 0 ? Math.max(...sectionMarks) : '',
                        totalWritten > 0 ? Math.min(...sectionMarks) : '',
                        failed,
                        totalWritten > 0 ? `${Math.round((failed / totalWritten) * 100)}%` : '0%',
                        passed,
                        totalWritten > 0 ? `${Math.round((passed / totalWritten) * 100)}%` : '0%',
                        levelDist[1], levelDist[2], levelDist[3], levelDist[4], levelDist[5], levelDist[6], levelDist[7],
                        totalWritten > 0 ? `${total17}/${totalWritten}` : ''
                        ]);
                    });

                    sectionRows.forEach(r => ws.addRow(r));

                    // Build OVERALL totals from unique students (not sum of section entries)
                    const sectionMaxById = {};
                    sections.forEach((s) => {
                        sectionMaxById[Number(s.id)] = parseFloat(s.max_mark) || 0;
                    });

                    const studentTotals = {};
                    marks.forEach((m) => {
                        const sid = String(m.student_id || '');
                        const secId = Number(m.section_id);
                        const markNum = parseFloat(m.mark);
                        const max = sectionMaxById[secId] || 0;
                        if (!sid || Number.isNaN(markNum) || max <= 0) return;

                        if (!studentTotals[sid]) {
                            studentTotals[sid] = { obtained: 0, max: 0 };
                        }
                        studentTotals[sid].obtained += markNum;
                        studentTotals[sid].max += max;
                    });

                    const overallPercents = [];
                    const overallLevelDist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
                    let overallPassed = 0;
                    let overallFailed = 0;

                    Object.values(studentTotals).forEach((t) => {
                        if (t.max <= 0) return;
                        const pct = (t.obtained / t.max) * 100;
                        overallPercents.push(pct);

                        const lvl = calculateLevel(pct);
                        overallLevelDist[lvl] += 1;

                        if (Math.round(pct) >= passMark) overallPassed += 1;
                        else overallFailed += 1;
                    });

                    const totalWrittenOverall = overallPercents.length;
                    const total17Overall = Object.values(overallLevelDist).reduce((a, b) => a + b, 0);
                    ws.addRow([
                    'OVERALL TOTAL',
                    students.length,
                    totalWrittenOverall,
                    totalWrittenOverall > 0 ? `${(overallPercents.reduce((a, b) => a + b, 0) / totalWrittenOverall).toFixed(1)}%` : '',
                    overallPercents.length > 0 ? `${Math.max(...overallPercents).toFixed(1)}%` : '',
                    overallPercents.length > 0 ? `${Math.min(...overallPercents).toFixed(1)}%` : '',
                    overallFailed,
                    totalWrittenOverall > 0 ? `${Math.round((overallFailed / totalWrittenOverall) * 100)}%` : '0%',
                    overallPassed,
                    totalWrittenOverall > 0 ? `${Math.round((overallPassed / totalWrittenOverall) * 100)}%` : '0%',
                    overallLevelDist[1], overallLevelDist[2], overallLevelDist[3], overallLevelDist[4], overallLevelDist[5], overallLevelDist[6], overallLevelDist[7],
                    totalWrittenOverall > 0 ? `${total17Overall}/${totalWrittenOverall}` : ''
                    ]);

                    // Mark the overall totals row so subsequent diagnostic/signature rows are not included in data styling
                    const overallTotalRow = ws.rowCount;

                    // Add diagnostic and signature sections to match teacher export formatting
                    ws.addRow([]);
                    ws.addRow(['DIAGNOSTIC ANALYSIS: (High frequency errors that were made in questions)']);
                    ws.addRow([]);
                    ws.addRow(['Problem area', '', '', '', 'Intervention', '', '', '', '', '', 'Time', '']);
                    for (let i = 0; i < 4; i++) {
                        ws.addRow(['', '', '', '', '', '', '', '', '', '', '', '']);
                    }

                    ws.addRow([]);
                    ws.addRow(['Signature of Teacher:', '', '', '', '', '', '', '', '', 'Date:', '', '', '', '']);
                    ws.addRow(['Signature of Departmental Head:', '', '', '', '', '', '', '', '', 'Date:', '', '', '', '']);

                    ws.columns = [
                    { width: 24 }, { width: 12 }, { width: 12 }, { width: 13 }, { width: 12 }, { width: 12 },
                    { width: 11 }, { width: 11 }, { width: 13 }, { width: 10 },
                    { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 },
                    { width: 18 }
                    ];

                    // Teacher-style formatting for readability (copied from teacher export)
                    ws.mergeCells(1, 1, 1, 18);
                    ws.getRow(1).height = 24;
                    ws.getRow(8).height = 22;

                    styleRange(ws, 1, 1, 1, 18, {
                        font: { bold: true, size: 12 },
                        fill: fillGrey('FFD9D9D9'),
                        border: thinBorder,
                        alignment: { horizontal: 'center', vertical: 'middle' }
                    });

                    styleRange(ws, 3, 5, 1, 1, {
                        font: { bold: true },
                        fill: fillGrey('FFEFEFEF'),
                        border: thinBorder,
                        alignment: { horizontal: 'left', vertical: 'middle' }
                    });

                    styleRange(ws, 3, 5, 2, 18, {
                        border: thinBorder,
                        alignment: { horizontal: 'left', vertical: 'middle' }
                    });

                    styleRange(ws, 8, 8, 1, 18, {
                        font: { bold: true },
                        fill: fillGrey('FFE5E5E5'),
                        border: thinBorder,
                        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true }
                    });

                    const tableHeaderRow = 8;
                    const tableFirstDataRow = 9;
                    const tableLastDataRow = overallTotalRow || ws.rowCount;
                    const diagTitleRow = tableLastDataRow + 2;
                    const diagHeaderRow = tableLastDataRow + 4;
                    const diagLastRow = diagHeaderRow + 4;
                    const sigRow1 = diagLastRow + 2;
                    const sigRow2 = diagLastRow + 3;

                    if (tableLastDataRow >= tableFirstDataRow) {
                        styleRange(ws, tableFirstDataRow, tableLastDataRow, 1, 18, {
                            border: thinBorder,
                            alignment: { horizontal: 'center', vertical: 'middle' }
                        });

                        styleRange(ws, tableFirstDataRow, tableLastDataRow, 1, 1, {
                            border: thinBorder,
                            alignment: { horizontal: 'left', vertical: 'middle' }
                        });
                    }

                    styleRange(ws, tableLastDataRow, tableLastDataRow, 1, 18, {
                        font: { bold: true },
                        fill: fillGrey('FFF5F5F5'),
                        border: thinBorder,
                        alignment: { horizontal: 'center', vertical: 'middle' }
                    });

                    styleRange(ws, tableLastDataRow, tableLastDataRow, 1, 1, {
                        font: { bold: true },
                        fill: fillGrey('FFF5F5F5'),
                        border: thinBorder,
                        alignment: { horizontal: 'left', vertical: 'middle' }
                    });

                    // Merge header and date/analysis cells to match teacher report
                    try { ws.mergeCells(6, 2, 6, 18); } catch (e) { }

                    // Diagnostic section merges
                    try {
                        ws.mergeCells(diagTitleRow, 1, diagTitleRow, 18);
                        ws.mergeCells(diagHeaderRow, 1, diagHeaderRow, 4);
                        ws.mergeCells(diagHeaderRow, 5, diagHeaderRow, 10);
                        ws.mergeCells(diagHeaderRow, 11, diagHeaderRow, 12);
                        for (let di = 1; di <= 4; di++) {
                            ws.mergeCells(diagHeaderRow + di, 1, diagHeaderRow + di, 4);
                            ws.mergeCells(diagHeaderRow + di, 5, diagHeaderRow + di, 10);
                            ws.mergeCells(diagHeaderRow + di, 11, diagHeaderRow + di, 12);
                            ws.getRow(diagHeaderRow + di).height = 45;
                        }
                    } catch (e) { }

                    // Signature row merges
                    try {
                        [sigRow1, sigRow2].forEach(r => {
                            ws.mergeCells(r, 1, r, 2);
                            ws.mergeCells(r, 3, r, 8);
                            ws.mergeCells(r, 11, r, 14);
                        });
                    } catch (e) { }

                } catch (err) {
                    console.error('Export subject error', subj.subject_name, err);
                }
            }
        }

        try {
            const buf = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const termLabel = terms.length === 1 ? `Term_${terms[0]}` : 'All_Terms';
            a.download = `HOD_Subjects_Detailed_${termLabel}_${new Date().toISOString().split('T')[0]}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            alert('Failed to generate Excel: ' + e.message);
        }
    };

    const exportToExcel = async () => exportToExcelForTerms([currentTerm]);
    const exportAllTermsToExcel = async () => exportToExcelForTerms([1, 2, 3, 4]);

    const exportOverviewToExcel = async () => {
        if (!analyticsData) return;

        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'Marksure HOD';
        workbook.created = new Date();

        const addTitle = (ws, title, subtitle) => {
            ws.addRow([title]);
            ws.addRow([subtitle]);
            ws.addRow([]);
            ws.getRow(1).font = { bold: true, size: 14 };
            ws.getRow(2).font = { italic: true };
        };

        const addRows = (ws, rows) => {
            rows.forEach((row) => ws.addRow(row));
        };

        const overviewWs = workbook.addWorksheet('Overview Stats');
        addTitle(overviewWs, 'HOD Overview Stats', `Term ${currentTerm}`);
        overviewWs.addRow(['Metric', 'Value']);
        addRows(overviewWs, [
            ['Total Students', analyticsData?.overall_stats?.total_students ?? 0],
            ['Active Students', analyticsData?.overall_stats?.active_students ?? 0],
            ['Active Subjects', analyticsData?.overall_stats?.total_subjects ?? 0],
            ['Assessments Logged', analyticsData?.overall_stats?.total_marks ?? 0],
            ['Top Performers (Grades)', filteredTopPerformersByGrade.reduce((sum, group) => sum + (group.students?.length || 0), 0)],
            ['Students Needing Support (Grades)', filteredSupportTableByGrade.reduce((sum, group) => sum + (group.students?.length || 0), 0)],
            ['Intervention by Subject Students', filteredSupportBySubject.reduce((sum, subject) => sum + (subject.students?.length || 0), 0)]
        ]);
        overviewWs.columns = [{ width: 34 }, { width: 18 }];

        const subjectRankingWs = workbook.addWorksheet('Subject Ranking');
        addTitle(subjectRankingWs, 'Subject Performance Ranking', 'Average and pass rate by subject');
        subjectRankingWs.addRow(['Subject', 'Avg %', 'Pass Rate %']);
        addRows(subjectRankingWs, subjectRankingData.map((row) => [row.subject_name, row.average_percentage, row.pass_rate ?? '']));
        subjectRankingWs.columns = [{ width: 34 }, { width: 12 }, { width: 14 }];

        const performanceStatusWs = workbook.addWorksheet('Performance Status');
        addTitle(performanceStatusWs, 'Performance Status Distribution', 'On track vs support ratio');
        performanceStatusWs.addRow(['Status', 'Students']);
        addRows(performanceStatusWs, performanceRatioData.map((row) => [row.name, row.value]));
        performanceStatusWs.columns = [{ width: 24 }, { width: 12 }];

        const trendWs = workbook.addWorksheet('Subject Trend');
        addTitle(trendWs, 'Subject Performance Comparison', 'Current term subject averages and pass rates');
        trendWs.addRow(['Subject', 'Average %', 'Pass Rate %']);
        addRows(trendWs, subjectTrendData.map((row) => [row.name, row.average, row.passRate]));
        trendWs.columns = [{ width: 34 }, { width: 12 }, { width: 14 }];

        const topWs = workbook.addWorksheet('Top Performers');
        addTitle(topWs, 'Top Performers per Grade', 'Filtered by the selected grade, if any');
        topWs.addRow(['Grade', 'Rank', 'Student', 'Class', 'Average %']);
        filteredTopPerformersByGrade.forEach((gradeGroup) => {
            (gradeGroup.students || []).forEach((student, index) => {
                topWs.addRow([
                    gradeGroup.grade_level,
                    index + 1,
                    student.name,
                    student.class_name || '',
                    student.average_percentage
                ]);
            });
        });
        topWs.columns = [{ width: 10 }, { width: 8 }, { width: 28 }, { width: 18 }, { width: 12 }];

        const supportWs = workbook.addWorksheet('Support by Grade');
        addTitle(supportWs, 'Students Needing Support per Grade', 'Overall average 55% and below');
        supportWs.addRow(['Grade', 'Rank', 'Student', 'Class', 'Average %']);
        filteredSupportTableByGrade.forEach((gradeGroup) => {
            (gradeGroup.students || []).forEach((student, index) => {
                supportWs.addRow([
                    gradeGroup.grade_level,
                    index + 1,
                    student.name,
                    student.class_name || '',
                    student.average_percentage
                ]);
            });
        });
        supportWs.columns = [{ width: 10 }, { width: 8 }, { width: 28 }, { width: 18 }, { width: 12 }];

        const interventionWs = workbook.addWorksheet('Intervention by Subject');
        addTitle(interventionWs, 'Intervention Required by Subject', 'Students scoring 55% and below in a subject');
        interventionWs.addRow(['Subject', 'Grade', 'Student', 'Class', 'Score %']);
        filteredSupportBySubject.forEach((subject) => {
            (subject.students || []).forEach((student) => {
                interventionWs.addRow([
                    subject.subject_name,
                    subject.grade_level,
                    student.name,
                    subject.class_name || '',
                    student.average_percentage
                ]);
            });
        });
        interventionWs.columns = [{ width: 30 }, { width: 10 }, { width: 28 }, { width: 18 }, { width: 12 }];

        const buf = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `HOD_Overview_Exports_${new Date().toISOString().split('T')[0]}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const exportMarkChangesToExcel = async () => {
        try {
            if (activityFromDate && activityToDate && activityFromDate > activityToDate) {
                alert('From date must be before or equal to To date.');
                return;
            }

            const params = new URLSearchParams();
            if (activityFromDate) params.set('start_date', activityFromDate);
            if (activityToDate) params.set('end_date', activityToDate);
            // Avoid stale cached responses in some browsers/proxies
            params.set('_ts', String(Date.now()));

            const authToken = localStorage.getItem('auth_token');
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/export_mark_changes.php?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });
            const activities = res?.data?.activities || [];

            // Fail-safe: apply date filter again on client to guarantee export matches selected range
            const filteredActivities = activities.filter((a) => {
                const rowDate = String(a?.updated_at || '').slice(0, 10);
                if (!rowDate) return false;
                if (activityFromDate && rowDate < activityFromDate) return false;
                if (activityToDate && rowDate > activityToDate) return false;
                return true;
            });

            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'Marksure HOD';
            workbook.created = new Date();
            const ws = workbook.addWorksheet('Mark Changes');

            ws.addRow(['Mark Changes Report']);
            ws.addRow(['Generated', new Date().toISOString()]);
            ws.addRow(['From', activityFromDate || 'All']);
            ws.addRow(['To', activityToDate || 'All']);
            ws.addRow([]);
            ws.addRow(['Student', 'Subject', 'Grade', 'Section', 'Action', 'Old Value', 'New Value', 'Max Mark', 'Percent', 'Changed By', 'Role', 'Date']);

            filteredActivities.forEach(a => {
                ws.addRow([
                    a.student_name || '',
                    a.subject_name || '',
                    a.grade_level ?? '',
                    a.section_name || '',
                    a.action_type || '',
                    a.old_value ?? '',
                    a.new_value ?? '',
                    a.max_mark ?? '',
                    a.average ?? '',
                    a.changed_by || '',
                    a.user_role || '',
                    a.updated_at || ''
                ]);
            });

            ws.columns = [
                { width: 24 }, { width: 24 }, { width: 8 }, { width: 18 }, { width: 12 }, { width: 10 },
                { width: 10 }, { width: 10 }, { width: 10 }, { width: 16 }, { width: 10 }, { width: 22 }
            ];

            const buf = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const rangeTag = activityFromDate || activityToDate
                ? `_${activityFromDate || 'start'}_to_${activityToDate || 'now'}`
                : '';
            a.download = `HOD_Mark_Changes${rangeTag}_${new Date().toISOString().split('T')[0]}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            alert('Failed to export mark changes: ' + (e?.message || 'Unknown error'));
        }
    };

    const exportRewardsToExcel = async () => {
        try {
            if (!rewardCriteria || !Object.keys(rewardCriteria).length) return;

            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'Marksure HOD';
            workbook.created = new Date();

            const selectedGradeLabel = selectedRewardGrade === 'all' ? 'All Grades' : `Grade ${selectedRewardGrade}`;
            const fileSafeLabel = selectedGradeLabel.replace(/[^a-z0-9]+/gi, '_');

            const writeHeader = (ws, title, subtitle, columns) => {
                ws.addRow([title]);
                ws.addRow([subtitle]);
                ws.addRow([]);
                ws.addRow(columns.map(col => col.header));
                ws.getRow(1).font = { bold: true, size: 14 };
                ws.getRow(2).font = { italic: true };
                ws.getRow(4).font = { bold: true };
                ws.getRow(4).fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFE5E5E5' }
                };
            };

            const buildRewardSheet = (sheetName, title, subtitle, rows, columns, mapRow) => {
                const ws = workbook.addWorksheet(sheetName);
                writeHeader(ws, title, subtitle, columns);
                rows.forEach((row) => ws.addRow(mapRow(row)));
                ws.columns = columns.map((col) => ({ header: col.header, width: col.width || 18 }));
                return ws;
            };

            const academicRows = filteredRewardRecipients.academicExcellence;
            const outstandingRows = filteredRewardRecipients.outstandingAchievement;
            const coloursRows = filteredRewardRecipients.seniorColours;

            const summaryWs = workbook.addWorksheet('Rewards Summary');
            summaryWs.addRow(['Rewards Summary']);
            summaryWs.addRow([`Grade Filter: ${selectedGradeLabel}`]);
            summaryWs.addRow([]);
            summaryWs.addRow(['Criterion', 'Description', 'Count']);
            [
                ['Academic Excellence', rewardCriteria.academic_excellence?.description || '', academicRows.length],
                ['Outstanding Academic Achievement', rewardCriteria.outstanding_academic_achievement?.description || '', outstandingRows.length],
                ['Senior Academic Colours', rewardCriteria.senior_academic_colours?.description || '', coloursRows.length]
            ].forEach((row) => summaryWs.addRow(row));
            summaryWs.columns = [{ width: 30 }, { width: 60 }, { width: 10 }];
            summaryWs.getRow(1).font = { bold: true, size: 14 };
            summaryWs.getRow(4).font = { bold: true };

            buildRewardSheet(
                'Acad Excellence',
                'Academic Excellence',
                'Subject-average based rewards (80%+).',
                academicRows,
                [
                    { header: 'Student', width: 24 },
                    { header: 'Grade', width: 10 },
                    { header: 'Class', width: 18 },
                    { header: 'Subject', width: 24 },
                    { header: 'Subject Avg %', width: 14 }
                ],
                (row) => [row.name, row.grade_level, row.class_name || '', row.subject_name || '', row.average_percentage]
            );

            buildRewardSheet(
                'Outstand Ach',
                'Outstanding Academic Achievement',
                'Overall-average based rewards (80%+).',
                outstandingRows,
                [
                    { header: 'Student', width: 24 },
                    { header: 'Grade', width: 10 },
                    { header: 'Class', width: 18 },
                    { header: 'Overall Avg %', width: 14 }
                ],
                (row) => [row.name, row.grade_level, row.class_name || '', row.average_percentage]
            );

            buildRewardSheet(
                'Senior Colours',
                'Senior Academic Colours',
                'Learners who qualify for Outstanding Academic Achievement.',
                coloursRows,
                [
                    { header: 'Student', width: 24 },
                    { header: 'Grade', width: 10 },
                    { header: 'Class', width: 18 },
                    { header: 'Overall Avg %', width: 14 }
                ],
                (row) => [row.name, row.grade_level, row.class_name || '', row.average_percentage]
            );

            const topTenWs = workbook.addWorksheet('Top Ten');
            topTenWs.addRow(['Top Ten Learners by Grade']);
            topTenWs.addRow([`Grade Filter: ${selectedGradeLabel}`]);
            topTenWs.addRow([]);
            topTenWs.addRow(['Grade', 'Rank', 'Student', 'Class', 'Average %']);
            filteredRewardRecipients.topTenGroups.forEach((gradeGroup) => {
                gradeGroup.students.forEach((student, index) => {
                    topTenWs.addRow([
                        gradeGroup.grade_level,
                        index + 1,
                        student.name,
                        student.class_name || '',
                        student.average_percentage
                    ]);
                });
            });
            topTenWs.columns = [{ width: 10 }, { width: 8 }, { width: 24 }, { width: 18 }, { width: 12 }];
            topTenWs.getRow(1).font = { bold: true, size: 14 };
            topTenWs.getRow(4).font = { bold: true };

            const buf = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `HOD_Rewards_${fileSafeLabel}_${new Date().toISOString().split('T')[0]}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            alert('Failed to export rewards: ' + (e?.message || 'Unknown error'));
        }
    };

    return (
        <div className="hod-dashboard">
            <div className="dashboard-header">
                <div>
                    <h1>{getGreeting()}</h1>
                    <p className="subtitle">School Performance Overview</p>
                </div>
                <div className="header-actions no-print">
                    <div className="header-actions-row">
                        <button
                            onClick={() => setActiveSection('overview')}
                            className="action-btn"
                            style={{ padding: '6px 10px', background: activeSection === 'overview' ? '#2e7d32' : undefined, color: activeSection === 'overview' ? '#fff' : undefined }}
                        >
                            Overview
                        </button>
                        <button
                            onClick={() => setActiveSection('rewards')}
                            className="action-btn"
                            style={{ padding: '6px 10px', background: activeSection === 'rewards' ? '#6d4c41' : undefined, color: activeSection === 'rewards' ? '#fff' : undefined }}
                        >
                            Rewards
                        </button>
                    </div>
                    <div className="header-actions-row">
                        {[1, 2, 3, 4].map(term => (
                            <button
                                key={term}
                                onClick={() => setCurrentTerm(term)}
                                className="action-btn"
                                style={{ padding: '6px 10px', background: currentTerm === term ? '#2e7d32' : undefined, color: currentTerm === term ? '#fff' : undefined }}
                            >
                                T{term}
                            </button>
                        ))}
                    </div>
                                        <div className="header-actions-row header-actions-secondary">
                                                                    <button onClick={exportOverviewToExcel} className="action-btn"><Icons.Download /> Overview Export</button>
                                                <button onClick={exportToExcel} className="action-btn"><Icons.Download /> Excel T{currentTerm}</button>
                                                <button onClick={exportAllTermsToExcel} className="action-btn"><Icons.Download /> Excel T1-4</button>
                                                <button onClick={exportMarkChangesToExcel} className="action-btn"><Icons.Download /> Mark Changes</button>
                                                <input
                        type="date"
                        value={activityFromDate}
                        onChange={(e) => setActivityFromDate(e.target.value)}
                        className="modern-input"
                        style={{ width: '150px' }}
                        title="Mark changes from date"
                    />
                    <input
                        type="date"
                        value={activityToDate}
                        onChange={(e) => setActivityToDate(e.target.value)}
                        className="modern-input"
                        style={{ width: '150px' }}
                        title="Mark changes to date"
                        />
                        <button onClick={fetchAnalytics} className="action-btn"><Icons.Refresh /> Refresh</button>
                       
                    </div>
                </div>
            </div>

            {activeSection === 'overview' && (
            <>
            {/* Insights */}
            {insights && insights.length > 0 && (
                <div className="insights-grid">
                    {insights.map((insight, index) => (
                        <div key={index} className={`insight-card ${insight.type}`}>
                            <div className="insight-icon">
                                {insight.type === 'warning' ? <Icons.Alert /> : <Icons.Info />}
                            </div>
                            <div>
                                <h3>{insight.title}</h3>
                                <p>{insight.message}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Key Metrics */}
            <div className="stats-cards">
                <div className="stat-card">
                    <div className="stat-icon-wrapper blue"><Icons.Student /></div>
                    <div className="stat-content">
                        <h3>{overall_stats.total_students}</h3>
                        <p>Total Students</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon-wrapper green"><Icons.Book /></div>
                    <div className="stat-content">
                        <h3>{uniqueSubjectCount}</h3>
                        <p>Active Subjects</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon-wrapper orange"><Icons.Check /></div>
                    <div className="stat-content">
                        <h3>{overall_stats.total_marks}</h3>
                        <p>Assessments Logged</p>
                    </div>
                </div>
            </div>

            {/* Charts Grid */}
            <div className="charts-section">

                <div className="chart-card">
                    <h3>Performance by Grade — Subject</h3>
                    <div style={{ marginBottom: 10, fontSize: 13, color: '#666', lineHeight: 1.4 }}>
                        Shows how the selected subject is performing across grades, comparing average score and pass rate side by side.
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ fontSize: 14, color: '#444' }}>
                            Select Subject:
                        </div>
                        <select value={selectedSubjectForGrade} onChange={(e) => setSelectedSubjectForGrade(e.target.value)} style={{ minWidth: 220 }}>
                            {subjectList.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={gradePerformanceData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="grade_level" tickFormatter={v => `Gr ${v}`} />
                            <YAxis domain={[0, 100]} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="average_percentage" fill="#4CAF50" name="Average (%)" />
                            <Bar dataKey="pass_rate" fill="#FF9800" name="Pass Rate (%)" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className="chart-card">
                    <h3>Performance by Grade (across all subjects)</h3>
                    <div style={{ marginBottom: 10, fontSize: 13, color: '#666', lineHeight: 1.4 }}>
                        Tracks overall grade performance across all subjects so you can quickly compare how each grade is doing school-wide.
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={performance_by_grade}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="grade_level" tickFormatter={v => `Gr ${v}`} />
                            <YAxis domain={[0, 100]} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="average_percentage" stroke="#4CAF50" strokeWidth={3} name="Average (%)" />
                            <Line type="monotone" dataKey="pass_rate" stroke="#FF9800" strokeWidth={3} name="Pass Rate (%)" />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div className="chart-card">
                    <h3>Subjects Distribution</h3>
                    <div style={{ marginBottom: 10, fontSize: 13, color: '#666', lineHeight: 1.4 }}>
                        Shows how the active subjects are distributed across grades, giving a quick sense of subject coverage in the school.
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={subjectDistributionData}
                                cx="50%" cy="50%"
                                innerRadius={60} outerRadius={100}
                                dataKey="value"
                                nameKey="name"
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                                {subjectDistributionData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => [`${value} subjects`, 'Count']} />
                            <Legend verticalAlign="bottom" formatter={(value) => value} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Subject Ranking Bar Chart */}
                <div className="chart-card">
                    <h3>Subject Performance Ranking</h3>
                    <div style={{ marginBottom: 10, fontSize: 13, color: '#666', lineHeight: 1.4 }}>
                        Ranks subjects from strongest to weakest based on their average performance, making it easy to spot problem areas.
                    </div>
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={subjectRankingData} layout="vertical" margin={{ top: 10, right: 30, left: 150, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis type="number" domain={[0, 100]} />
                            <YAxis type="category" dataKey="subject_name" width={140} tick={{ fontSize: 12 }} />
                            <Tooltip formatter={(value) => [`${value}%`, 'Avg']} />
                            <Bar dataKey="average_percentage" fill="#2196F3" name="Average %" radius={[0, 8, 8, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Top Performers vs Support Students Ratio */}
                <div className="chart-card">
                    <h3>Performance Status Distribution</h3>
                    <div style={{ marginBottom: 10, fontSize: 13, color: '#666', lineHeight: 1.4 }}>
                        Breaks the learner population into students on track and students needing support so you can see the overall balance at a glance.
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={performanceRatioData}
                                cx="50%" cy="50%"
                                innerRadius={50} outerRadius={100}
                                paddingAngle={2}
                                dataKey="value"
                                nameKey="name"
                                label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                            >
                                {performanceRatioData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => [`${value} students`, 'Count']} />
                            <Legend verticalAlign="bottom" />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Subject Performance Line Chart (Trend) */}
                <div className="chart-card">
                    <h3>Subject Performance Comparison (Current Term)</h3>
                    <div style={{ marginBottom: 10, fontSize: 13, color: '#666', lineHeight: 1.4 }}>
                        Compares subject averages and pass rates within the current term to highlight which subjects are leading or lagging.
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={subjectTrendData} margin={{ top: 10, right: 30, left: 0, bottom: 60 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
                            <YAxis domain={[0, 100]} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="average" stroke="#4CAF50" strokeWidth={2.5} name="Average %" dot={{ r: 4 }} />
                            <Line type="monotone" dataKey="passRate" stroke="#FF9800" strokeWidth={2.5} name="Pass Rate %" dot={{ r: 4 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Tables Section */}
            <div className="tables-grid">

                {/* Top Performers */}
                <div className="table-card">
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <h3>Top Performers per Grade</h3>
                        <select value={selectedTopGrade} onChange={(e) => setSelectedTopGrade(e.target.value)} style={{ minWidth: 200, padding: '8px 10px' }}>
                            <option value="all">All grades</option>
                            {gradeOptions.map((grade) => (
                                <option key={grade} value={grade}>Grade {grade}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ padding: '0 20px 12px', fontSize: '0.85rem', color: '#666' }}>
                        Calculation: overall average across all subjects for each learner.
                    </div>
                    {filteredTopPerformersByGrade.length > 0 ? filteredTopPerformersByGrade.map((gradeGroup) => (
                        <div key={gradeGroup.grade_level} style={{ padding: '0 20px 20px' }}>
                            <h4 style={{ margin: '16px 0 10px', color: '#2c3e50' }}>Grade {gradeGroup.grade_level}</h4>
                            <table className="modern-table">
                                <thead>
                                    <tr>
                                        <th>Rank</th>
                                        <th>Student</th>
                                        <th>Class</th>
                                        <th>Grade</th>
                                        <th>Avg</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {gradeGroup.students.map((s, i) => (
                                        <tr key={s.student_id}>
                                            <td><span className="rank-badge">#{i + 1}</span></td>
                                            <td>{s.name}</td>
                                            <td>{s.class_name || ''}</td>
                                            <td>{s.grade_level}</td>
                                            <td className="highlight-text">{s.average_percentage}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )) : (
                        <div style={{ padding: '20px' }}>No top performers found for this filter.</div>
                    )}
                </div>

                {/* Needs Support */}
                <div className="table-card">
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <h3>⚠️ Students Needing Support per Grade</h3>
                        <select value={selectedSupportTableGrade} onChange={(e) => setSelectedSupportTableGrade(e.target.value)} style={{ minWidth: 200, padding: '8px 10px' }}>
                            <option value="all">All grades</option>
                            {gradeOptions.map((grade) => (
                                <option key={grade} value={grade}>Grade {grade}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ padding: '0 20px 12px', fontSize: '0.85rem', color: '#666' }}>
                        Calculation: overall average across all subjects for each learner.
                    </div>
                    {filteredSupportTableByGrade.length > 0 ? filteredSupportTableByGrade.map((gradeGroup) => (
                        <div key={gradeGroup.grade_level} style={{ padding: '0 20px 20px' }}>
                            <h4 style={{ margin: '16px 0 10px', color: '#2c3e50' }}>Grade {gradeGroup.grade_level}</h4>
                            <table className="modern-table">
                                <thead>
                                    <tr>
                                        <th>Priority</th>
                                        <th>Student</th>
                                        <th>Class</th>
                                        <th>Grade</th>
                                        <th>Avg</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {gradeGroup.students.map((s, i) => (
                                        <tr key={s.student_id}>
                                            <td>
                                                <span className={`priority-badge ${i < 3 ? 'high' : 'med'}`}>
                                                    {i < 3 ? 'High' : 'Med'}
                                                </span>
                                            </td>
                                            <td>{s.name}</td>
                                            <td>{s.class_name || ''}</td>
                                            <td>{s.grade_level}</td>
                                            <td className="danger-text">{s.average_percentage}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )) : (
                        <div style={{ padding: '20px' }}>No students found for this grade filter.</div>
                    )}
                </div>
            </div>

            </>
            )}

            {activeSection === 'rewards' && rewardCriteria && Object.keys(rewardCriteria).length > 0 && (
                <div className="rewards-section">
                    <div className="rewards-section-header">
                        <div>
                            <h2>Rewards & Award Criteria</h2>
                            <p>Select a grade to see which learners qualify for each reward.</p>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                            <select value={selectedRewardGrade} onChange={(e) => setSelectedRewardGrade(e.target.value)} style={{ minWidth: 200, padding: '8px 10px' }}>
                                <option value="all">All grades</option>
                                {gradeOptions.map((grade) => (
                                    <option key={grade} value={grade}>Grade {grade}</option>
                                ))}
                            </select>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }} title="Compute awards using all terms for the full year">
                                <input type="checkbox" checked={rewardAnnual} onChange={(e) => { setRewardAnnual(e.target.checked); }} />
                                <span style={{ fontSize: 13 }}>Annual awards (use all terms)</span>
                            </label>
                            <button onClick={exportRewardsToExcel} className="action-btn">
                                <Icons.Download /> Export Rewards
                            </button>
                        </div>
                    </div>

                    <div className="rewards-grid">
                        {[
                            {
                                key: 'academic_excellence',
                                title: rewardCriteria.academic_excellence?.title || 'Academic Excellence',
                                description: rewardCriteria.academic_excellence?.description || 'Learners who attain 80% or above in a subject.',
                                recipients: filteredRewardRecipients.academicExcellence,
                                showSubject: true
                            },
                            {
                                key: 'outstanding_academic_achievement',
                                title: rewardCriteria.outstanding_academic_achievement?.title || 'Outstanding Academic Achievement',
                                description: rewardCriteria.outstanding_academic_achievement?.description || 'Learners who attain 80% or above overall.',
                                recipients: filteredRewardRecipients.outstandingAchievement,
                                showSubject: false
                            },
                            {
                                key: 'senior_academic_colours',
                                title: rewardCriteria.senior_academic_colours?.title || 'Senior Academic Colours',
                                description: rewardCriteria.senior_academic_colours?.description || 'Awarded to learners who achieve Outstanding Academic Achievement.',
                                recipients: filteredRewardRecipients.seniorColours,
                                showSubject: false
                            }
                        ].map((award) => {
                            const visibleRecipients = award.recipients.slice(0, 6);
                            const remaining = Math.max(award.recipients.length - visibleRecipients.length, 0);
                            return (
                                <div key={award.key} className="reward-card">
                                    <div className="support-header">
                                        <h4>{award.title}</h4>
                                        <span className="count-badge">{award.recipients.length}</span>
                                    </div>
                                    <div style={{ marginBottom: '12px', fontSize: '0.85rem', color: '#666' }}>
                                        {award.key === 'academic_excellence'
                                            ? 'Calculation: subject mark only. A learner qualifies when a subject average is 80% or above.'
                                            : 'Calculation: overall average across all subjects. A learner qualifies when the overall average is 80% or above.'}
                                    </div>
                                    <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '12px' }}>{award.description}</div>
                                    <div className="student-list">
                                        {visibleRecipients.length > 0 ? visibleRecipients.map((recipient) => (
                                            <div key={`${award.key}-${recipient.student_id}-${recipient.subject_id || 'overall'}`} className="student-row">
                                                <span>
                                                    {recipient.name}
                                                    {recipient.class_name ? (<span className="class-inline">• {recipient.class_name}</span>) : null}
                                                    {award.showSubject && recipient.subject_name ? (<span className="class-inline">• {recipient.subject_name}</span>) : null}
                                                </span>
                                                <span className="score">{recipient.average_percentage}%</span>
                                            </div>
                                        )) : (
                                            <div className="student-row">
                                                <span>No learners currently meet this criterion.</span>
                                                <span className="score"> </span>
                                            </div>
                                        )}
                                        {remaining > 0 && (
                                            <div className="student-row">
                                                <span>+{remaining} more...</span>
                                                <span className="score"> </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="rewards-top-ten">
                        <h3 style={{ marginBottom: '16px' }}>Top Ten Learners by Grade</h3>
                        <div className="rewards-grid">
                            {filteredRewardRecipients.topTenGroups.map((gradeGroup) => (
                                <div key={gradeGroup.grade_level} className="reward-card">
                                    <div className="support-header">
                                        <h4>Grade {gradeGroup.grade_level}</h4>
                                        <span className="count-badge">{gradeGroup.students.length} Students</span>
                                    </div>
                                    <div className="student-list">
                                        {gradeGroup.students.map((student, index) => (
                                            <div key={student.student_id} className="student-row">
                                                <span><span className="rank-badge">#{index + 1}</span> {student.name}{student.class_name ? (<span className="class-inline">• {student.class_name}</span>) : null}</span>
                                                <span className="score">{student.average_percentage}%</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {activeSection === 'overview' && (
            <>
            {/* Intervention By Subject */}
            {students_needing_support_by_subject && students_needing_support_by_subject.length > 0 && (
                <div className="detailed-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                        <h3 style={{ margin: 0 }}>Intervention Required by Subject</h3>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <select value={selectedSupportSubjectGrade} onChange={(e) => {
                                setSelectedSupportSubjectGrade(e.target.value);
                                setSelectedSupportSubject('all');
                            }} style={{ minWidth: 180, padding: '8px 10px' }}>
                                <option value="all">All grades</option>
                                {gradeOptions.map((grade) => (
                                    <option key={grade} value={grade}>Grade {grade}</option>
                                ))}
                            </select>
                            <select value={selectedSupportSubject} onChange={(e) => setSelectedSupportSubject(e.target.value)} style={{ minWidth: 260, padding: '8px 10px' }}>
                                <option value="all">All subjects</option>
                                {supportSubjectOptions.map((subject) => (
                                    <option key={subject.value} value={subject.value}>{subject.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="support-grid">
                        {filteredSupportBySubject.map((subject) => {
                            const visibleStudents = (subject.students || []).slice(0, 8);
                            const remaining = Math.max((subject.students || []).length - visibleStudents.length, 0);
                            return (
                                <div key={subject.subject_id} className="support-card">
                                    <div className="support-header">
                                        <h4>{subject.subject_name} (Gr {subject.grade_level})</h4>
                                        <span className="class-badge">{subject.class_name}</span>
                                        <span className="count-badge">{subject.total_students} Students</span>
                                    </div>
                                    <div style={{ marginBottom: '12px', fontSize: '0.85rem', color: '#666' }}>
                                        Calculation: subject average only for this subject, not the learner's overall average.
                                    </div>
                                    <div className="student-list">
                                        {visibleStudents.map(s => (
                                            <div key={`${subject.subject_id}-${s.student_id}`} className="student-row">
                                                    <span>{s.name} {s.grade_level ? (<span className="class-inline">• Grade {s.grade_level}</span>) : null}</span>
                                                    <span className="score">{s.average_percentage}%</span>
                                            </div>
                                        ))}
                                        {remaining > 0 && (
                                            <div className="student-row">
                                                <span>+{remaining} more...</span>
                                                <span className="score"> </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            </>
            )}
        </div>
    );
}